/**
 * City Planning Tours — mirrors iOS TourPlanningView (For You / Viator + MCP search).
 */

import React, { useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Segmented, Spin, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionFeature } from '../../contexts/SubscriptionContext';
import { cityPlanningEn } from '../../content/cityPlanning.en';
import {
  fetchFirstPageAndCache,
  getCachedViatorProducts,
  isOnline,
  type ViatorProduct,
} from '../../services/viatorProductsService';
import {
  mcpSearchIsoDate,
  searchViatorMcp,
} from '../../services/viatorMcpSearchService';
import { getAccessibleTrip, updateTrip } from '../../services/tripsService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import { visibleViatorProducts } from '../../utils/tourCatalogSearch';
import {
  stayArrivalDeparture,
} from '../../utils/cityPlanningStay';
import {
  applyAddPlanItemToTrip,
  buildUserPlanSelection,
} from '../../utils/planItemSave';
import { buildTourItineraryItem } from '../../utils/tourItemSave';
import { effectiveCanEdit } from '../../utils/tripAccessPolicy';
import { captureSanitizedException, isPostHogConfigured } from '../../lib/posthog';
import TourDateSlotModal, {
  type TourDateSlotSelection,
} from '../../components/TourDateSlotModal/TourDateSlotModal';
import { CityPlanningChrome } from './cityPlanningShared';
import styles from './CityPlanning.module.css';

type ToursTab = 'forYou' | 'viator';

const CityPlanningTours: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const [searchParams] = useSearchParams();
  const cityVisitId = searchParams.get('cityVisitId')?.trim() ?? '';
  const { user } = useAuth();
  const includesAIPlan = useSubscriptionFeature('aiPlan');

  const [tripRevision, setTripRevision] = useState(0);
  const [viatorProducts, setViatorProducts] = useState<ViatorProduct[]>([]);
  const [tourQuery, setTourQuery] = useState('');
  const [submittedTourQuery, setSubmittedTourQuery] = useState<string | null>(null);
  const [mcpProducts, setMcpProducts] = useState<ViatorProduct[] | null>(null);
  const [mcpSessionId, setMcpSessionId] = useState<string | null>(null);
  const [isMcpSearching, setIsMcpSearching] = useState(false);
  const [mcpError, setMcpError] = useState<string | null>(null);
  const [tab, setTab] = useState<ToursTab>('viator');
  const [pickerProduct, setPickerProduct] = useState<ViatorProduct | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const trip = useMemo(() => {
    if (!user?.sub || !tripId) return null;
    return getAccessibleTrip(user.sub, tripId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub, tripId, tripRevision]);

  useCanonicalTripUrl(trip);

  const meta = cityVisitId ? trip?.cityVisitMeta?.[cityVisitId] : undefined;
  const cityName =
    (cityVisitId && trip?.cityNames?.[cityVisitId]) || cityPlanningEn.untitledCity;
  const canEdit = Boolean(trip && user?.sub && effectiveCanEdit(trip, user.sub));
  const { arrivalDate, departureDate } = useMemo(() => {
    if (!trip || !cityVisitId) return { arrivalDate: '', departureDate: '' };
    return stayArrivalDeparture(trip, cityVisitId);
  }, [trip, cityVisitId]);

  const forYouProducts = useMemo(
    () =>
      viatorProducts.filter(
        (p) => Array.isArray(p.matchedInterests) && p.matchedInterests.length > 0
      ),
    [viatorProducts]
  );

  useEffect(() => {
    if (forYouProducts.length > 0) setTab('forYou');
  }, [forYouProducts.length]);

  useEffect(() => {
    if (!user?.sub || !cityVisitId) return;
    const cached = getCachedViatorProducts(user.sub, cityVisitId);
    if (cached.length > 0) {
      setViatorProducts(cached);
      return;
    }
    void fetchFirstPageAndCache(user.sub, cityVisitId)
      .then(setViatorProducts)
      .catch(() => undefined);
  }, [user?.sub, cityVisitId]);

  const visibleResults = useMemo(() => {
    if (tab === 'forYou' && !submittedTourQuery) {
      return forYouProducts;
    }
    return visibleViatorProducts({
      cached: tab === 'forYou' ? forYouProducts : viatorProducts,
      query: tourQuery,
      submittedQuery: submittedTourQuery,
      mcpProducts,
    });
  }, [
    tab,
    forYouProducts,
    viatorProducts,
    tourQuery,
    submittedTourQuery,
    mcpProducts,
  ]);

  const handleTourQueryChange = (value: string): void => {
    setTourQuery(value);
    setMcpError(null);
    if (
      submittedTourQuery &&
      value.trim().toLowerCase() !== submittedTourQuery.trim().toLowerCase()
    ) {
      setMcpProducts(null);
      setSubmittedTourQuery(null);
    }
  };

  const handleMcpTourSearch = async (): Promise<void> => {
    if (!isOnline()) {
      setMcpError(cityPlanningEn.tourSearchOffline);
      return;
    }
    const term = tourQuery.trim();
    if (!term || !cityName) return;
    setIsMcpSearching(true);
    setMcpError(null);
    try {
      const response = await searchViatorMcp({
        searchTerm: term,
        cityName,
        countryName: meta?.countryName ?? '',
        startDate: mcpSearchIsoDate(meta?.arrivalDate ?? arrivalDate),
        endDate: mcpSearchIsoDate(
          meta?.departureDate ?? departureDate,
          true
        ),
        cityVisitId,
        sessionId: mcpSessionId,
      });
      setMcpProducts(response.products);
      setSubmittedTourQuery(term);
      setMcpSessionId(response.sessionId);
      if (isPostHogConfigured) {
        posthog.capture('tour_search_completed', {
          result_count: response.products.length,
          continued_session: Boolean(mcpSessionId),
        });
      }
    } catch (err) {
      captureSanitizedException('tour_search', err);
      setMcpError(
        err instanceof Error && err.message
          ? err.message
          : cityPlanningEn.tourSearchError
      );
    } finally {
      setIsMcpSearching(false);
    }
  };

  const handleConfirm = (selection: TourDateSlotSelection): void => {
    if (!trip || !pickerProduct || !user?.sub || !canEdit) {
      message.info(cityPlanningEn.viewerCannotSave);
      return;
    }
    const item = buildTourItineraryItem({
      tour: pickerProduct,
      dateYmd: selection.dateYmd,
      slot: selection.slot,
      cityName,
    });
    const sel = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: pickerProduct.title,
      selectedSlot: selection.slot,
      selectedDate: selection.dateYmd,
    });
    const updated = updateTrip(
      user.sub,
      trip.id,
      applyAddPlanItemToTrip(trip, {
        cityVisitId,
        dateYmd: selection.dateYmd,
        item,
        selection: sel,
      })
    );
    if (updated) {
      setTripRevision((n) => n + 1);
      message.success(cityPlanningEn.itemAdded);
    }
    setPickerOpen(false);
    setPickerProduct(null);
  };

  if (!includesAIPlan) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="info" message={cityPlanningEn.noSubscription} showIcon />
        </div>
      </div>
    );
  }

  if (!tripId || !cityVisitId) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="warning" message={cityPlanningEn.missingCity} showIcon />
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="error" message={cityPlanningEn.missingTrip} showIcon />
        </div>
      </div>
    );
  }

  const showingMcp =
    Boolean(submittedTourQuery) &&
    tourQuery.trim().toLowerCase() === submittedTourQuery?.trim().toLowerCase() &&
    mcpProducts != null;

  const segmentOptions = forYouProducts.length
    ? [
        { label: cityPlanningEn.toursForYou, value: 'forYou' as const },
        { label: cityPlanningEn.toursViator, value: 'viator' as const },
      ]
    : [{ label: cityPlanningEn.toursViator, value: 'viator' as const }];

  return (
    <CityPlanningChrome
      trip={trip}
      userId={user?.sub}
      cityName={cityName}
      cityVisitId={cityVisitId}
      section="tours"
      canEdit={canEdit}
    >
      {!canEdit ? (
        <Alert
          type="info"
          message={cityPlanningEn.readOnly}
          showIcon
          className={styles.statusBanner}
        />
      ) : null}

      {segmentOptions.length > 1 ? (
        <div className={styles.segmentRow}>
          <Segmented
            block
            options={segmentOptions}
            value={tab}
            onChange={(v) => setTab(v as ToursTab)}
          />
        </div>
      ) : null}

      {isOnline() ? (
        <div className={styles.tourSearchRow}>
          <Input
            allowClear
            placeholder={cityPlanningEn.tourSearchPlaceholder}
            value={tourQuery}
            onChange={(e) => handleTourQueryChange(e.target.value)}
            onPressEnter={() => void handleMcpTourSearch()}
            disabled={isMcpSearching}
          />
          <Button
            type="primary"
            loading={isMcpSearching}
            disabled={!tourQuery.trim()}
            onClick={() => void handleMcpTourSearch()}
          >
            {cityPlanningEn.tourSearchAction}
          </Button>
        </div>
      ) : (
        <p className={styles.emptySlot}>{cityPlanningEn.tourSearchOffline}</p>
      )}

      {mcpError ? <Alert type="error" message={mcpError} showIcon /> : null}
      {isMcpSearching ? (
        <div className={styles.loading}>
          <Spin /> {cityPlanningEn.tourSearchSearching}
        </div>
      ) : null}
      {tourQuery.trim() && !showingMcp && isOnline() ? (
        <p className={styles.emptySlot}>{cityPlanningEn.tourSearchLocalHint}</p>
      ) : null}

      {visibleResults.length === 0 ? (
        <p className={styles.emptySlot}>
          {tourQuery.trim()
            ? cityPlanningEn.tourSearchEmpty
            : cityPlanningEn.toursEmpty}
        </p>
      ) : (
        visibleResults.map((product) => {
          const price =
            product.price?.startingPrice != null && product.price.startingPrice > 0
              ? `${product.price.currency} ${Math.round(product.price.startingPrice)}`
              : null;
          return (
            <article key={product.productCode} className={styles.tourCard}>
              {product.imageUrl ? (
                <img
                  className={styles.tourImage}
                  src={product.imageUrl}
                  alt=""
                />
              ) : (
                <div className={styles.tourImagePlaceholder} aria-hidden />
              )}
              <div>
                <p className={styles.cardTitle}>{product.title}</p>
                <p className={styles.cardMeta}>
                  {cityPlanningEn.tourSource}
                  {price ? ` · ${price}` : ''}
                </p>
                {product.description ? (
                  <p className={styles.cardDesc}>{product.description}</p>
                ) : null}
                <div className={styles.cardActions}>
                  {product.productUrl ? (
                    <Button
                      size="small"
                      href={product.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {cityPlanningEn.bookTour}
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    type="primary"
                    disabled={!canEdit}
                    onClick={() => {
                      setPickerProduct(product);
                      setPickerOpen(true);
                    }}
                  >
                    {cityPlanningEn.addToTrip}
                  </Button>
                </div>
              </div>
            </article>
          );
        })
      )}

      <TourDateSlotModal
        open={pickerOpen}
        tour={pickerProduct}
        arrivalDate={arrivalDate}
        departureDate={departureDate}
        initialDate={arrivalDate}
        onCancel={() => {
          setPickerOpen(false);
          setPickerProduct(null);
        }}
        onConfirm={handleConfirm}
      />
    </CityPlanningChrome>
  );
};

export default CityPlanningTours;
